// Lineage — Visual dependency graph (live API)
// Loads from /api/lineage/:domain/:recordId. Default to a recently updated
// record so the user lands on a useful graph immediately.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { LineageGraph, LineageEdge, LineageNode } from "@/lib/types";

const DOMAIN_COLORS: Record<string, string> = {
  instruments: "var(--rust-500)",
  counterparties: "var(--forest-500)",
  currencies: "var(--ochre-400)",
  countries: "var(--info)",
  industries: "var(--sand-600)",
  systems: "var(--sand-600)",
};

function colorFor(domain?: string) {
  return (domain && DOMAIN_COLORS[domain]) || "var(--md-outline)";
}

function edgeColor(c?: number) {
  const v = c ?? 1;
  if (v >= 0.9) return "var(--forest-500)";
  if (v >= 0.75) return "var(--ochre-400)";
  return "var(--rust-500)";
}

// Parse "domain:id" or fallback to a record id.
function splitId(id: string): { domain?: string; rid: string } {
  const i = id.indexOf(":");
  if (i < 0) return { rid: id };
  return { domain: id.slice(0, i), rid: id.slice(i + 1) };
}

// Assign a "level" to each node based on graph distance from root.
function assignLevels(rootId: string, nodes: LineageNode[], edges: LineageEdge[]): Map<string, number> {
  const levels = new Map<string, number>();
  levels.set(rootId, 1);
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (!adj.has(e.from)) adj.set(e.from, []);
    if (!adj.has(e.to)) adj.set(e.to, []);
    adj.get(e.from)!.push(e.to);
    adj.get(e.to)!.push(e.from);
  }
  const queue: string[] = [rootId];
  while (queue.length) {
    const cur = queue.shift()!;
    const d = levels.get(cur)!;
    for (const n of adj.get(cur) ?? []) {
      if (!levels.has(n)) { levels.set(n, d + 1); queue.push(n); }
    }
  }
  for (const n of nodes) if (!levels.has(n.id)) levels.set(n.id, 1);
  return levels;
}

export function Lineage() {
  const { recordId: paramRecordId } = useParams();
  const [domain, setDomain] = useState<string>("instruments");
  const [recordId, setRecordId] = useState<string>(paramRecordId ?? "");
  const [graph, setGraph] = useState<LineageGraph | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [layout, setLayout] = useState<"hierarchical" | "radial">("hierarchical");
  const [depth, setDepth] = useState<1 | 2 | 3>(2);
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Array<{ id: string; domainId?: string; label: string }>>([]);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  // If no record provided, default to most-recently-updated instrument.
  useEffect(() => {
    if (recordId) return;
    api.records.list("instruments", { pageSize: 1, sort: "updatedAt:desc" })
      .then(p => {
        if (p.items?.[0]) { setRecordId(p.items[0].id); setDomain(p.items[0].domainId); }
      })
      .catch(() => { /* ignored */ });
  }, [recordId]);

  // Load lineage when domain/record/depth changes.
  useEffect(() => {
    if (!recordId) return;
    setLoading(true); setError(null);
    api.lineage.get(domain, recordId, depth)
      .then(g => { setGraph(g); setSelected(g.root); })
      .catch(e => setError(e instanceof Error ? e.message : "Failed to load lineage"))
      .finally(() => setLoading(false));
  }, [domain, recordId, depth]);

  // Search-as-you-type
  useEffect(() => {
    if (!search) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      api.search(search, { limit: 8 })
        .then(hits => setSearchResults(hits.map(h => ({ id: h.id, domainId: h.domainId, label: h.label }))))
        .catch(() => setSearchResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const positions = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    if (!graph) return map;
    const W = 900, H = 540;
    const root = graph.root;
    const levels = assignLevels(root, graph.nodes, graph.edges);
    if (layout === "hierarchical") {
      const buckets = new Map<number, LineageNode[]>();
      for (const n of graph.nodes) {
        const l = levels.get(n.id) ?? 1;
        if (!buckets.has(l)) buckets.set(l, []);
        buckets.get(l)!.push(n);
      }
      const maxLevel = Math.max(...buckets.keys(), 1);
      for (const [lvl, list] of buckets) {
        const xStep = W / Math.max(1, maxLevel);
        list.forEach((n, i) => {
          const yStep = H / (list.length + 1);
          map.set(n.id, { x: xStep * (lvl - 0.5), y: yStep * (i + 1) });
        });
      }
    } else {
      const cx = W / 2, cy = H / 2;
      graph.nodes.forEach((n, i) => {
        const lvl = levels.get(n.id) ?? 1;
        const r = 40 + lvl * 80;
        const angle = (i / Math.max(1, graph.nodes.length)) * Math.PI * 2;
        map.set(n.id, { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
      });
    }
    return map;
  }, [graph, layout]);

  const filteredNodes = useMemo(() => {
    if (!graph) return [];
    if (!search) return graph.nodes;
    const q = search.toLowerCase();
    return graph.nodes.filter(n => n.label.toLowerCase().includes(q) || n.id.toLowerCase().includes(q));
  }, [graph, search]);

  const visibleNodeIds = new Set(filteredNodes.map(n => n.id));
  const visibleEdges = (graph?.edges ?? []).filter(e => visibleNodeIds.has(e.from) && visibleNodeIds.has(e.to));
  const selectedNode = graph?.nodes.find(n => n.id === selected) ?? null;
  const downstream = visibleEdges.filter(e => e.from === selected).length;
  const upstream = visibleEdges.filter(e => e.to === selected).length;

  function pickSearchResult(r: { id: string; domainId?: string; label: string }) {
    const { domain: d, rid } = splitId(r.id);
    const dom = r.domainId ?? d ?? domain;
    setDomain(dom);
    setRecordId(rid);
    setSearch("");
    setSearchResults([]);
    navigate(`/lineage/${rid}`);
  }

  const fitToView = () => setZoom(1);
  const exportSvg = () => {
    if (!svgRef.current) return;
    const blob = new Blob([svgRef.current.outerHTML], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "lineage.svg"; a.click();
    URL.revokeObjectURL(url);
    toast("Lineage graph exported as SVG", "ok");
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr 320px", gap: 16, padding: 16, height: "calc(100vh - 76px)" }}>
      <aside style={panel}>
        <div style={eyebrow}>EXPLORE</div>
        <div style={{ font: "var(--t-headline-sm)", fontStyle: "italic", fontWeight: 400, marginBottom: 12 }}>Lineage</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search records…" style={input} />
        {searchResults.length > 0 && (
          <div style={{ marginTop: 8, background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-md)" }}>
            {searchResults.map(r => (
              <button key={r.id} onClick={() => pickSearchResult(r)} style={{
                width: "100%", textAlign: "left", appearance: "none", border: "none", background: "transparent",
                padding: "8px 10px", cursor: "pointer", font: "var(--t-body-sm)", color: "var(--md-on-surface)",
              }}>{r.label} <span style={{ color: "var(--md-on-surface-var)", font: "var(--t-mono-sm)" }}>({r.id})</span></button>
            ))}
          </div>
        )}
        <div style={{ marginTop: 14, font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>
          Current root: <span className="font-mono" style={{ color: "var(--md-on-surface)" }}>{domain}/{recordId || "—"}</span>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ ...eyebrow, marginBottom: 6 }}>DEPTH</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[1, 2, 3].map(d => (
              <button key={d} onClick={() => setDepth(d as 1 | 2 | 3)}
                style={{ ...pillBtn, ...(depth === d ? pillBtnActive : {}) }}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div style={{ ...eyebrow, marginTop: 16, marginBottom: 6 }}>LEGEND</div>
        <div style={{ display: "grid", gap: 6, font: "var(--t-body-sm)" }}>
          <LegendDot color="var(--forest-500)" label="High confidence (≥90%)" />
          <LegendDot color="var(--ochre-400)" label="Medium (75–90%)" />
          <LegendDot color="var(--rust-500)" label="Low (<75%)" />
        </div>
      </aside>

      <section style={{ ...panel, padding: 0, overflow: "hidden", position: "relative" }}>
        <div style={toolbar}>
          <button style={pillBtn} onClick={() => setZoom(z => Math.min(z + 0.1, 2))} aria-label="Zoom in">+</button>
          <button style={pillBtn} onClick={() => setZoom(z => Math.max(z - 0.1, 0.4))} aria-label="Zoom out">−</button>
          <button style={pillBtn} onClick={fitToView}>Fit</button>
          <span style={divider} />
          <button style={{ ...pillBtn, ...(layout === "hierarchical" ? pillBtnActive : {}) }} onClick={() => setLayout("hierarchical")}>Hierarchical</button>
          <button style={{ ...pillBtn, ...(layout === "radial" ? pillBtnActive : {}) }} onClick={() => setLayout("radial")}>Radial</button>
          <span style={divider} />
          <button style={pillBtn} onClick={exportSvg}>Export SVG</button>
        </div>
        <div tabIndex={0} role="region" aria-label="Lineage graph viewport" style={{ width: "100%", height: "calc(100% - 56px)", overflow: "auto" }} className="scroll">
          {loading && <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>Loading lineage…</div>}
          {error && <div style={{ padding: 24, color: "var(--md-error)" }}>{error}</div>}
          {!loading && !error && graph && (
            <svg ref={svgRef} viewBox="0 0 900 560"
              style={{ width: 900 * zoom, height: 560 * zoom, display: "block" }}>
              <defs>
                {Object.keys(DOMAIN_COLORS).map(d => (
                  <marker key={d} id={`arrow-${d}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0,0 L10,5 L0,10 z" fill={DOMAIN_COLORS[d]} />
                  </marker>
                ))}
              </defs>
              {visibleEdges.map((e, i) => {
                const a = positions.get(e.from);
                const b = positions.get(e.to);
                if (!a || !b) return null;
                const mx = (a.x + b.x) / 2;
                const path = `M${a.x},${a.y} C${mx},${a.y} ${mx},${b.y} ${b.x},${b.y}`;
                const isSel = selected === e.from || selected === e.to;
                return (
                  <g key={i} opacity={isSel || !selected ? 1 : 0.25}>
                    <path d={path} stroke={edgeColor(e.confidence)} strokeWidth={isSel ? 2.5 : 1.5} fill="none" />
                    <text x={mx} y={(a.y + b.y) / 2 - 4} textAnchor="middle" style={{ font: "var(--t-label-sm)", fill: "var(--md-on-surface-var)" }}>
                      {e.kind}
                    </text>
                  </g>
                );
              })}
              {filteredNodes.map(n => {
                const p = positions.get(n.id);
                if (!p) return null;
                const isSel = selected === n.id;
                const color = colorFor(n.domain ?? n.kind);
                return (
                  <g key={n.id} transform={`translate(${p.x},${p.y})`} style={{ cursor: "pointer" }} onClick={() => setSelected(n.id)}>
                    <ellipse rx={isSel ? 56 : 50} ry={isSel ? 26 : 22}
                      fill="var(--md-sc-lowest)" stroke={color} strokeWidth={isSel ? 2.5 : 1.5}
                      style={{ filter: "drop-shadow(0 4px 8px rgba(28,23,20,.1))" }} />
                    <text textAnchor="middle" y={-2} style={{ font: "var(--t-mono-sm)", fontWeight: 600, fill: color }}>{n.id.split(":").pop()?.slice(0, 10)}</text>
                    <text textAnchor="middle" y={12} style={{ font: "var(--t-label-sm)", fill: "var(--md-on-surface)" }}>
                      {n.label.length > 18 ? n.label.slice(0, 18) + "…" : n.label}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </section>

      <aside style={panel}>
        {selectedNode ? (
          <>
            <div style={eyebrow}>{(selectedNode.domain ?? selectedNode.kind).toUpperCase()}</div>
            <div style={{ font: "var(--t-headline-sm)", fontStyle: "italic", fontWeight: 400 }}>{selectedNode.label}</div>
            <div style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)", marginTop: 4 }}>{selectedNode.id}</div>
            <div style={{ marginTop: 16, padding: 12, background: "var(--md-secondary-container)", borderRadius: "var(--r-md)", color: "var(--md-on-secondary-container)" }}>
              <div style={{ font: "var(--t-label-md)" }}>Impact</div>
              <div style={{ font: "var(--t-headline-md)", marginTop: 4 }}>
                <span style={{ fontStyle: "italic", fontWeight: 400 }}>{downstream}</span> downstream
              </div>
              <div style={{ font: "var(--t-body-sm)", marginTop: 4 }}>{upstream} upstream sources</div>
            </div>
            <button style={{ ...filledBtn, marginTop: 16, width: "100%" }} onClick={() => {
              const { domain: d, rid } = splitId(selectedNode.id);
              if (selectedNode.kind !== "record") { toast(`${selectedNode.label} is an external ${selectedNode.kind}`, "info"); return; }
              const dom = selectedNode.domain ?? d;
              if (!dom) return;
              navigate(`/browse/${dom}/${rid}`);
            }}>Open record</button>
            <div style={{ ...eyebrow, marginTop: 16, marginBottom: 6 }}>EDGES</div>
            <div style={{ display: "grid", gap: 6, font: "var(--t-body-sm)", maxHeight: 200, overflowY: "auto" }} className="scroll">
              {visibleEdges.filter(e => e.from === selected || e.to === selected).map((e, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", background: "var(--md-sc-low)", borderRadius: "var(--r-sm)" }}>
                  <span style={{ font: "var(--t-mono-sm)" }}>{e.from === selected ? "→ " : "← "}{e.from === selected ? e.to : e.from}</span>
                  <span style={{ color: edgeColor(e.confidence) }}>{Math.round((e.confidence ?? 1) * 100)}%</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ color: "var(--md-on-surface-var)" }}>Select a node to inspect</div>
        )}
      </aside>
    </div>
  );
}

const panel: CSSProperties = { background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)", padding: 16, overflow: "auto" };
const eyebrow: CSSProperties = { font: "var(--t-label-sm)", color: "var(--md-tertiary)", letterSpacing: "0.12em" };
const input: CSSProperties = { width: "100%", padding: "8px 12px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-low)", color: "var(--md-on-surface)", font: "var(--t-body-md)", marginTop: 8 };
const pillBtn: CSSProperties = { padding: "6px 12px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-lowest)", color: "var(--md-on-surface)", cursor: "pointer", font: "var(--t-label-md)" };
const pillBtnActive: CSSProperties = { background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)", borderColor: "transparent" };
const filledBtn: CSSProperties = { padding: "10px 20px", borderRadius: "var(--r-pill)", border: "none", background: "var(--md-primary)", color: "var(--md-on-primary)", cursor: "pointer", font: "var(--t-label-lg)" };
const toolbar: CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: 10, borderBottom: "1px solid var(--md-outline-var)", background: "var(--md-sc-low)" };
const divider: CSSProperties = { width: 1, height: 20, background: "var(--md-outline-var)" };

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 10, height: 10, borderRadius: 999, background: color }} />
      <span>{label}</span>
    </div>
  );
}

export default Lineage;
